import { FilterOption, FilterOptionValueType } from '../types/odataFilterOption';
import { generateODataQueryString } from '../helpers/odataQueryParam';
import { OdataComparator } from '../types/odataComparator';

type FilterFragment = {
  value: string | number;
  operator: 'and' | 'or';
};

const {
  Any,
  Contains,
  Equals,
  GreaterThan,
  GreaterThanOrEqualTo,
  In,
  LessThan,
  LessThanOrEqualTo,
  NotEquals
} = OdataComparator;
const { DateType } = FilterOptionValueType;

export class FilterBuilder {
  private fragments: FilterFragment[] = [];

  filterBy = (
    property: string,
    comparator: OdataComparator,
    value: string | string[] | number | boolean | undefined | null,
    filterOption?: FilterOption
  ) => {
    if (value !== undefined || comparator === Any) {
      const formattedFragment = this.formatFilterValue(
        property,
        comparator,
        value !== undefined ? value : '',
        filterOption
      );

      if (formattedFragment) {
        const queryString = generateODataQueryString(
          formattedFragment.property,
          formattedFragment.comparator,
          formattedFragment.value !== undefined ? formattedFragment.value : '',
          filterOption
        );

        this.fragments.push({ value: queryString, operator: 'and' });
      }
    }
    return this;
  };

  and = (predicate: (filter: FilterBuilder) => FilterBuilder) => {
    const queryString = predicate(new FilterBuilder()).toQueryString();

    if (queryString) {
      this.fragments.push({ value: `(${queryString})`, operator: 'and' });
    }

    return this;
  };

  or = (predicate: (filter: FilterBuilder) => FilterBuilder) => {
    const queryString = predicate(new FilterBuilder()).toQueryString();

    if (queryString) {
      this.fragments.push({ value: `(${queryString})`, operator: 'or' });
    }

    return this;
  };

  toQueryString = () => {
    return !this.fragments || this.fragments.length < 1
      ? ''
      : this.fragments.map((f, index) => (index === 0 ? f.value : `${f.operator} ${f.value}`)).join(' ');
  };

  private formatFilterValue = (
    property: string,
    comparator: OdataComparator,
    value: string | string[] | number | boolean | null,
    filterOption?: FilterOption
  ) => {
    const isDateValue = filterOption?.valueType === DateType;
    switch (comparator) {
      case Any:
        return {
          property,
          comparator,
          value: ''
        };
      case Contains:
        return {
          property,
          comparator,
          value:
            value === null
              ? value
              : isNaN(Number(value))
              ? `${escapeSingleQuote(value.toString().toLowerCase())}`
              : Number(value)
        };
      case Equals:
      case NotEquals:
        return {
          property,
          comparator,
          value:
            value === null
              ? value
              : typeof value === 'boolean' || typeof value === 'number'
              ? value
              : isGuid(value.toString()) || isDateValue
              ? `${escapeSingleQuote(value.toString())}`
              : `'${escapeSingleQuote(value.toString())}'`
        };
      case In:
        const formattedValue = Array.isArray(value) ? value.map(item => `'${item}'`).join(',') : `'${value}'`;

        return { property, comparator, value: formattedValue };
      case GreaterThan:
      case GreaterThanOrEqualTo:
      case LessThan:
      case LessThanOrEqualTo:
        return {
          property,
          comparator,
          value
        };
      default:
        return typeof value === 'string' && comparator ? { property, comparator, value } : undefined;
    }
  };
}

const isGuid = (value: string) => {
  const regexGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return regexGuid.test(value);
};

const escapeSingleQuote = (input: string) => {
  return input.replace(/'/g, "''");
};
